import * as React from 'react'
import NPCardSurfaceErrorBoundary from '@components/card-surface-error-boundary'
import {CardSurfaceLoadingIndicator} from '@components/card-surface-loading-indicator'
import {QueryClientProvider} from '@tanstack/react-query'
import {reactQueryClient, ServiceFamiliesTypes} from '@applications/utility-methods'
import {SurfaceList, SurfaceListItem} from 'telstra-ui/components/list/List'
import {Surface} from 'telstra-ui/components/surface/Surface'
import {SvgIcon} from 'telstra-ui/components/svg/Icon'
import {useNavigate} from 'react-router-dom'
import {getServicePerformanceFlagName} from '@applications'

import './yourproducts-card.scss'

import {
  Routes,
  Application as ServicePerformanceApplication,
} from '@applications/service-performance'
import {useAppDispatch, useAppSelector} from '@hooks/redux-hooks'
import {loadServiceTypesInt} from '@telstra-connect/service-performance/methods/load-service-types-int'
import {loading} from '@redux/actions/loading'
import {getCompanyId} from '@telstra-connect/config/config'
import {Components as YourProductsAndServicesActivityTrackingComponents} from '@telstra-connect/your-products-and-services/activity-tracking/'
import {Application} from '@applications/your-products-and-services'
import {AsyncActionState} from '@telstra-connect/standard-actions/async-action'
import {headerName} from '@telstra-connect/utils/helper/common-static-data'

const YPSSurfaceContainer = () => {
  const dispatch = useAppDispatch()
  const yourProductServicesHomePageInt = getServicePerformanceFlagName(
    'yourProductServicesHomePageInt',
  )
  const newAllServicePage = getServicePerformanceFlagName('ableAllServicePageFeature')

  const navUrl = newAllServicePage
    ? ServicePerformanceApplication.routes.getPageSubpath(Routes.AllServices)
    : ServicePerformanceApplication.routes.getInitialPagePath()

  const globalNetworksUrl = ServicePerformanceApplication.routes.getPageSubpath(
    Routes.getAdaptiveNetworksDetailInt(ServiceFamiliesTypes.ADAPTIVENETWORK),
  )
  const dataCentreCloudUrl = ServicePerformanceApplication.routes.getPageSubpath(
    Routes.getAdaptiveNetworksDetailInt(ServiceFamiliesTypes.DATACENTRECLOUD),
  )

  const navigate = useNavigate()
  const cardTitle = headerName.yourProductsAndServicesInt

  const handleGlobalNetworks = () => {
    navigate(globalNetworksUrl)
    Application.tracker.trackLink(
      YourProductsAndServicesActivityTrackingComponents.dashboardComponentInt,
      `Clicked on item heading Adaptive networks from dashboard page`,
    )
  }
  const handledAllServices = () => {
    navigate(navUrl)
    Application.tracker.trackLink(
      YourProductsAndServicesActivityTrackingComponents.dashboardComponentInt,
      `Clicked on item heading All services from dashboard page`,
    )
  }

  const handledDataCentreCloud = () => {
    navigate(dataCentreCloudUrl)
    Application.tracker.trackLink(
      YourProductsAndServicesActivityTrackingComponents.dashboardComponentInt,
      `Clicked on item heading Data centre and cloud from dashboard page`,
    )
  }

  const user = useAppSelector((state) => state.user)
  const tokens = useAppSelector((state) => state.tokens)
  const userManagement = useAppSelector((state) => state.userManagement)

  const eligibleServiceTypesForGlobalNetworkTile: string[] =
    (getServicePerformanceFlagName('eligibleServiceTypesForGlobalNetworkTile') as string[]) || []

  const eligibleGlobalProductCodeForDCCTile: string[] =
    (getServicePerformanceFlagName('eligibleGlobalProductCodeForDCC') as string[]) || []

  const adaptiveNetworkFeature = getServicePerformanceFlagName('adaptiveNetworkFeature')
  const dataCentreCloudDetails = getServicePerformanceFlagName('dataCentreCloudDetails')

  const authorizedRequestState = {
    user,
    tokens,
    userManagement,
  }
  const companyId = getCompanyId(
    authorizedRequestState.tokens,
    authorizedRequestState.user,
    authorizedRequestState.userManagement,
  )
  React.useEffect(() => {
    dispatch(loading(false))
  }, [])

  React.useEffect(() => {
    dispatch(loadServiceTypesInt(companyId))
  }, [])

  const serviceTypesList = useAppSelector((state) => state?.servicePerformance?.serviceTypesInt)

  const hasEligibleServiceType = serviceTypesList?.data?.serviceTypes.some((serviceType) =>
    eligibleServiceTypesForGlobalNetworkTile.includes(serviceType),
  )
  const hasEligibleProductCodeForDCCTile = serviceTypesList?.data?.serviceTypes.some(
    (serviceType) => eligibleGlobalProductCodeForDCCTile.includes(serviceType),
  )

  const handleErrorCallback = () => window.location.reload()

  const renderError = (
    <>
      <div className="tc-np-card-error">
        <div className="tc-np-card-error__body">
          <SvgIcon
            name={'picto-empty-state'}
            className="tc-telstra-icon tc-np-card-error__body__icon"
          />
          <div className="tc-np-card-error__body__icon__error" id="ui-np-card-error-text">
            {'Loading error'}
          </div>
          <div className="tc-np-card-error__body__icon__text" id="ui-np-card-error-subtext">
            There was an error loading this app.
          </div>
        </div>
      </div>
      <div
        id="ui-np-card-refresh"
        className="tc-np-card-error__button tl-list-im-button"
        onClick={handleErrorCallback}
      >
        <SvgIcon
          name={'icon-system-refresh'}
          className="tc-footer-refresh-icon"
          id="ui-np-card-refresh-icon"
        />
        <span className="tc-footer-text" id="ui-np-card-refresh-text">
          Refresh
        </span>
      </div>
    </>
  )
  const navigateYourProductsServicesHomePage = () => {
    if (yourProductServicesHomePageInt) {
      navigate(
        Application.routes.getPageSubpath(`${Routes.yourProductsServicesInt}#products-services`),
      )
      Application.tracker.trackLink(
        YourProductsAndServicesActivityTrackingComponents.yourProductsServicesHomePage,
        `Clicked on item heading from dashboard page for ${YourProductsAndServicesActivityTrackingComponents.yourProductsServicesHomePage}`,
      )
    }
  }

  const renderHeader = () => (
    <div
      className="tl-heading__container tc-np-heading-padding"
      onClick={() => {
        navigateYourProductsServicesHomePage()
      }}
    >
      <div className="__heading ui-np-card-heading" id="ui-np-card-heading">
        {cardTitle}
      </div>
      {yourProductServicesHomePageInt && (
        <div className="tc-np-icon-right">
          <SvgIcon className="tc-np-svg-arrow" name="icon-system-arrow1-right" />
        </div>
      )}
    </div>
  )
  const renderBody = () => {
    return (
      <>
        <div className="tc-np-your-products-surface-body-padding">
          {hasEligibleServiceType && adaptiveNetworkFeature && (
            <SurfaceListItem
              itemHeading={'Adaptive networks'}
              itemClassName="ui-global-networks-card"
              itemIcon={'icon-system-chevron-right'}
              itemDesc={''}
              onClick={() => {
                handleGlobalNetworks()
              }}
              skeltonLoading={
                serviceTypesList?.data?.serviceTypes.length === 0 &&
                serviceTypesList?.status === AsyncActionState.Initiated
                  ? true
                  : false
              }
            />
          )}
          {hasEligibleProductCodeForDCCTile && dataCentreCloudDetails && (
            <SurfaceListItem
              itemHeading={'Data centre and cloud'}
              itemClassName="ui-global-card-data-center-cloud"
              itemIcon={'icon-system-chevron-right'}
              itemDesc={''}
              onClick={() => {
                handledDataCentreCloud()
              }}
              skeltonLoading={
                serviceTypesList?.data?.serviceTypes.length === 0 &&
                serviceTypesList?.status === AsyncActionState.Initiated
                  ? true
                  : false
              }
            />
          )}

          <SurfaceListItem
            itemHeading={'All services'}
            itemClassName="ui-global-card-view-all-services"
            itemIcon={'icon-system-chevron-right'}
            itemDesc={''}
            onClick={() => {
              handledAllServices()
            }}
            skeltonLoading={
              serviceTypesList?.data?.serviceTypes.length === 0 &&
              serviceTypesList?.status === AsyncActionState.Initiated
                ? true
                : false
            }
          />
        </div>
        {serviceTypesList?.status === AsyncActionState.Failure && {renderError}}
      </>
    )
  }
  return (
    <QueryClientProvider client={reactQueryClient}>
      <NPCardSurfaceErrorBoundary yourProductAndServices={true}>
        <React.Suspense fallback={<CardSurfaceLoadingIndicator />}>
          <div
            className="tc-np-your-products-surface-container tc-np-your-products-surface-container-padding"
            style={{height: '424px'}}
          >
            <Surface>
              <SurfaceList header={renderHeader()} body={renderBody()} />
            </Surface>
          </div>
        </React.Suspense>
      </NPCardSurfaceErrorBoundary>
    </QueryClientProvider>
  )
}

export default YPSSurfaceContainer
